// @ts-check
import { advance, createState, hasFloor } from './player.js';
import { instantiateLevel } from './level-state.js';
import { querySolids } from './geometry.js';
import { defeatEnemy, updateEnemies } from './enemies.js';
import { playerBody, canStand, canCrawl, loseUpgrade, updatePower } from './power.js';
import { dispense, updateRewards } from './rewards.js';
import { updateShots } from './projectiles.js';
import { WORLD } from './tuning.js';
import { signal, updateMechanisms } from './mechanisms.js';
import { counterBoss, updateBoss } from './bosses.js';
export const TILE = WORLD.tile;
export const BODY = { width: WORLD.playerWidth, height: WORLD.playerHeight };
export const ART_HEIGHT = WORLD.artHeight;
/** @typedef {import('./types.js').Level} Level */
/** @typedef {import('./types.js').Solid} Solid */
/** @typedef {import('./player.js').Position} Position */
/** @param {import('./types.js').LevelDefinition} definition */
export function createLevel(definition) { return instantiateLevel(definition); }

/** @param {Level} level @param {Position} state */
export function restartLevel(level, state) {
  Object.assign(level, createLevel(level.definition));
  Object.assign(state, createState(level.spawn));
}

/** Physical impact is independent of breakability and one-time rewards.
 * @param {Level} level @param {Solid} solid @param {import('./types.js').EventSink} [emit] @param {Position} [state]
 */
export function hitBlock(level, solid, emit, state = createState()) {
  if (!solid.active || solid.bump > 0) return;
  solid.bump = 0.16;
  if (solid.signal) { signal(level, solid.signal, emit); if (solid.signal === 'boss') counterBoss(level, emit); }
  if (solid.kind === 'cache' && state.power !== 'base') solid.active = false;
  const fresh = solid.kind === 'node' && !solid.used;
  if (fresh) { solid.used = true; dispense(level, solid, state, emit); }
  emit?.({ type: !solid.active ? 'block-broken' : fresh ? 'block-activated' : 'block-bumped', x: solid.x + solid.width / 2, y: solid.y + solid.height, entityId: solid.id });
  for (const enemy of level.enemies) {
    if (enemy.mode !== 'dead' && enemy.x + enemy.width > solid.x && enemy.x < solid.x + solid.width && Math.abs(enemy.y - solid.y - solid.height) < 3) defeatEnemy(enemy, emit, true);
  }
}

/** @param {Level} level @param {import('./types.js').EventSink} [emit] @param {Position} [state] @returns {import('./player.js').Bounds} */
export function levelBounds(level, emit, state) {
  const arena = level.boss && !['dormant', 'defeated'].includes(level.boss.phase) ? level.boss.definition : null;
  return { width: (arena ? arena.right : level.width - 14) - (state ? playerBody(state).width : BODY.width), left: arena ? arena.left : 14, height: level.height, body: state ? playerBody(state) : BODY,
    getBody: () => state ? playerBody(state) : BODY, canStand: () => !state || canStand(level, state), canCrawl: (direction) => !!state && canCrawl(level, state, direction),
    solids: level.solids, gaps: level.gaps, speed: WORLD.levelSpeed, gravity: level.definition.quantum ? .58 : 1, querySolids: (left, right) => querySolids(level, left, right),
    onHeadHit: (solid) => hitBlock(level, solid, emit, state),
    onLand: (solid) => { if (state && state.power !== 'base') hitBlock(level, solid, emit, state); } };
}

/** Highest surface below QP, or null above a pit. @param {Level} level @param {Position} state */
export function supportHeight(level, state) {
  const BODY = playerBody(state);
  let height = hasFloor(state.x, BODY.width, level) && state.y >= 0 ? 0 : null;
  for (const solid of querySolids(level, state.x, state.x + BODY.width)) {
    const top = solid.y + solid.height;
    if (solid.active && top <= state.y + 0.1 && state.x + BODY.width > solid.x && state.x < solid.x + solid.width) height = Math.max(height ?? -Infinity, top);
  }
  return height;
}

/** Ground that stays put: the floor, or an unbreakable static solid under all of QP.
 * Moving and breakable surfaces are never remembered as a place to return to.
 * @param {Level} level @param {Position} state
 */
function onSolidGround(level, state) {
  const { width } = playerBody(state);
  if (Math.abs(state.y) < 0.001) return hasFloor(state.x, width, level) && !level.gaps.some(([start, end]) => state.x + width + TILE > start && state.x - TILE < end);
  const under = level.solids.filter(s => s.active && !s.hidden && s.kind !== 'cache' && Math.abs(s.y + s.height - state.y) < 0.001 && state.x + width > s.x && state.x < s.x + s.width);
  return under.length > 0 && under.some(s => s.x <= state.x) && under.some(s => s.x + s.width >= state.x + width);
}

/** Assist returns a falling QP to the last solid ground it stood on, at the cost of one upgrade.
 * @param {Level} level @param {Position} state @param {import("./types.js").EventSink} [emit]
 */
function catchFall(level, state, emit) {
  const safe = level.safe ?? { x: level.spawn, y: 0 };
  Object.assign(state, { x: safe.x, y: safe.y, vx: 0, vy: 0, grounded: true, jumps: 0, pound: 'none', poundTime: 0, impact: 0, boost: 0, crouching: false, sliding: false });
  loseUpgrade(state);
  emit?.({ type: 'damaged', x: state.x, y: state.y, reason: 'assist-fall' });
}

/** Advance the complete level in small steps, including contact with fast shells.
 * @param {Level} level @param {Position} state @param {number} direction @param {number} elapsed
 * @param {import('./player.js').Controls} controls @param {import("./types.js").EventSink} [emit]
 */
export function advanceLevel(level, state, direction, elapsed, controls, emit) {
  if (level.status !== 'playing') return;
  if (direction || state.vx || !state.grounded || controls.down) level.started = true;
  if (!level.started) return;
  let remaining = Math.min(Math.max(elapsed, 0), 0.05);
  while (remaining > 1e-9 && level.status === 'playing') {
    const dt = Math.min(remaining, 1 / 120);
    remaining -= dt;
    level.time += dt;
    const oldY = state.y;
    updatePower(level, state, dt);
    updateMechanisms(level, state, dt, emit);
    advance(state, direction, dt, levelBounds(level, emit, state), controls);
    updateRewards(level, state, dt, emit);
    updateShots(level, dt, emit);
    for (const solid of level.solids) solid.bump = Math.max(0, solid.bump - dt);
    updateEnemies(level, state, oldY, dt, emit);
    updateBoss(level, state, oldY, dt, emit);
    if (level.assist && state.grounded && onSolidGround(level, state)) level.safe = { x: state.x, y: state.y };
    if (state.y < WORLD.fallLimit && level.assist) catchFall(level, state, emit);
    else if (state.y < WORLD.fallLimit) { level.status = 'dead'; level.reason = 'Fell into a grid fracture'; state.vx = 0; }
    if (level.definition.bandwidthGoal && level.signalHeight === null && level.status === 'playing' && state.x + BODY.width >= level.finish && state.x <= level.finish + 40 && state.y <= 360 && (!level.boss || level.boss.phase === 'defeated')) {
      level.signalHeight = Math.max(0, Math.min(320, state.y));
      emit?.({ type: 'signal-captured', x: level.finish, y: level.signalHeight, amount: 500 + Math.floor(level.signalHeight / 80) * 500 });
    }
    // Enter the near side of a pocket's return cloud, one tile before its center.
    const exit = level.finish - (level.definition.quantum && !level.definition.quantumFinale ? WORLD.tile : 0);
    if (level.status === 'playing' && state.x >= exit && state.grounded && (!level.boss || level.boss.phase === 'defeated')) { level.status = 'won'; state.vx = 0; }
  }
  if (level.status === 'dead') emit?.({ type: 'failed', x: state.x, y: state.y, reason: level.reason });
  if (level.status === 'won') emit?.({ type: 'completed', x: state.x, y: state.y });
}
